import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Video, RefreshCw, Loader2, ScrollText, CheckCircle2, ListChecks,
  Trash2, Info, ExternalLink, PlayCircle, FlaskConical, AlertCircle,
} from "lucide-react";

const SOURCE_LABEL = {
  zoom: "Zoom",
  meet: "Google Meet",
};

const SOURCE_STYLES = {
  zoom: "bg-sky-500/15 text-sky-600 border-sky-500/40",
  meet: "bg-emerald-500/15 text-emerald-600 border-emerald-500/40",
};

const STATUS_STYLES = {
  processed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/40",
  missing_transcript: "bg-amber-500/15 text-amber-600 border-amber-500/40",
  empty_transcript: "bg-amber-500/15 text-amber-600 border-amber-500/40",
  download_failed: "bg-red-500/15 text-red-600 border-red-500/40",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Meetings() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [meetStatus, setMeetStatus] = useState(null);
  const [zoomStatus, setZoomStatus] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selected, setSelected] = useState(null); // full row for drawer
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [simTopic, setSimTopic] = useState("Sprint Planning");
  const [simVtt, setSimVtt] = useState(SAMPLE_VTT);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    (async () => {
      await Promise.all([loadRows(), loadStatuses()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadRows() {
    try {
      const r = await api.get("/meetings", { params: { limit: 100 } });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error("Couldn't load meetings", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  }

  async function loadStatuses() {
    try {
      const [z, m] = await Promise.all([
        api.get("/meetings/zoom/status"),
        api.get("/meetings/meet/status"),
      ]);
      setZoomStatus(z.data);
      setMeetStatus(m.data);
    } catch {
      /* ignore — status is decorative */
    }
  }

  const filtered = useMemo(() => {
    if (sourceFilter === "all") return rows;
    return rows.filter((r) => r.source === sourceFilter);
  }, [rows, sourceFilter]);

  async function scanMeet() {
    setScanning(true);
    try {
      const r = await api.post("/meetings/meet/scan");
      const { ingested = 0, skipped = 0, scanned = 0, hint } = r.data || {};
      if (scanned === 0 && hint) {
        toast.info("No transcripts found", { description: hint });
      } else {
        toast.success(`Scanned ${scanned} · Ingested ${ingested} · Skipped ${skipped}`);
      }
      await loadRows();
    } catch (e) {
      toast.error("Meet scan failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally {
      setScanning(false);
    }
  }

  async function runSimulate(e) {
    e?.preventDefault?.();
    if (!simVtt.trim()) {
      toast.error("VTT content is required");
      return;
    }
    setSimulating(true);
    try {
      await api.post("/meetings/zoom/simulate", { topic: simTopic, vtt: simVtt });
      toast.success("Simulated meeting processed");
      setSimulateOpen(false);
      await loadRows();
    } catch (err) {
      toast.error("Simulation failed", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSimulating(false);
    }
  }

  async function deleteRow(row) {
    if (!window.confirm(`Delete transcript "${row.topic}"? This also removes any auto-captured decisions from personal memory.`)) return;
    try {
      await api.delete(`/meetings/${row.id}`);
      toast.success("Deleted");
      setSelected(null);
      await loadRows();
    } catch (e) {
      toast.error("Delete failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="meetings-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-semibold flex items-center gap-3">
            <Video className="w-8 h-8 text-fuchsia-500" /> Meetings
          </h1>
          <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-2xl">
            Auto-captured decisions and action items from your Zoom + Google Meet transcripts.
            Uses native cloud transcripts — no audio ever leaves your provider.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            data-testid="meetings-scan-meet"
            onClick={scanMeet}
            disabled={scanning || !meetStatus?.connected}
            title={!meetStatus?.connected ? "Connect Google Calendar in Settings first" : "Scan Drive for Meet transcripts"}
          >
            {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Scan Google Meet
          </Button>
          <Button
            variant="outline"
            data-testid="meetings-simulate-open"
            onClick={() => setSimulateOpen(true)}
          >
            <FlaskConical className="w-4 h-4 mr-2" />
            Simulate Zoom
          </Button>
        </div>
      </header>

      {/* Provider status strip */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ProviderCard
          name="Zoom"
          icon={<Video className="w-5 h-5 text-sky-500" />}
          configured={!!zoomStatus?.configured}
          extra={
            <span className={`text-xs ${zoomStatus?.webhook_verifiable ? "text-emerald-600" : "text-amber-600"}`}>
              {zoomStatus?.webhook_verifiable ? "Webhook signature verifiable" : "Webhook secret not set"}
            </span>
          }
          hint="Add ZOOM_CLIENT_ID / SECRET / ACCOUNT_ID / WEBHOOK_SECRET_TOKEN in backend .env, then point Zoom Marketplace webhook to /api/meetings/zoom/webhook."
        />
        <ProviderCard
          name="Google Meet"
          icon={<Video className="w-5 h-5 text-emerald-500" />}
          configured={!!meetStatus?.connected}
          extra={
            meetStatus?.connected
              ? <span className="text-xs text-emerald-600">Connected as {meetStatus.google_email}</span>
              : <span className="text-xs text-muted-foreground">Connect Google Calendar in Settings to unlock</span>
          }
          hint="Meet transcripts require Google Workspace Business Standard or higher. Scanner reads the transcript Doc from Drive."
        />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Filter:</span>
        {["all", "zoom", "meet"].map((s) => (
          <button
            key={s}
            onClick={() => setSourceFilter(s)}
            data-testid={`meetings-filter-${s}`}
            className={`px-3 py-1 rounded-full border transition ${
              sourceFilter === s
                ? "bg-fuchsia-500/15 border-fuchsia-500/60 text-fuchsia-600"
                : "border-border hover:bg-muted"
            }`}
          >
            {s === "all" ? "All" : SOURCE_LABEL[s]}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">{filtered.length} shown</span>
      </div>

      {/* List */}
      {loading ? (
        <MeetingsSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState onSimulate={() => setSimulateOpen(true)} onScan={scanMeet} meetConnected={!!meetStatus?.connected} />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.map((row) => (
            <MeetingCard key={row.id} row={row} onClick={() => setSelected(row)} />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="meeting-detail-drawer">
          {selected && <MeetingDetail row={selected} onDelete={() => deleteRow(selected)} canDelete={selected.user_id === user?.id} />}
        </DialogContent>
      </Dialog>

      {/* Simulate dialog */}
      <Dialog open={simulateOpen} onOpenChange={setSimulateOpen}>
        <DialogContent className="max-w-2xl" data-testid="meeting-simulate-dialog">
          <DialogHeader>
            <DialogTitle>Simulate a Zoom transcript</DialogTitle>
            <DialogDescription>
              Paste WebVTT content to test the pipeline without waiting for a real Zoom webhook.
              The transcript will be attributed to you.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={runSimulate} className="space-y-3">
            <div>
              <label className="text-sm font-medium">Topic</label>
              <Input
                value={simTopic}
                onChange={(e) => setSimTopic(e.target.value)}
                data-testid="simulate-topic-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">VTT content</label>
              <Textarea
                value={simVtt}
                onChange={(e) => setSimVtt(e.target.value)}
                rows={10}
                className="font-mono text-xs"
                data-testid="simulate-vtt-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSimulateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={simulating} data-testid="simulate-submit-btn">
                {simulating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Process transcript
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProviderCard({ name, icon, configured, extra, hint }) {
  return (
    <div className="bento p-4" data-testid={`provider-${name.toLowerCase().replace(/\s+/g, "-")}-card`}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-medium">{name}</span>
        <span className={`ml-auto text-xs px-2 py-0.5 rounded-full border ${configured
          ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/40"
          : "bg-slate-500/15 text-slate-500 border-slate-500/40"}`}>
          {configured ? "Ready" : "Not configured"}
        </span>
      </div>
      <div className="mt-2 text-xs">
        {extra}
      </div>
      {!configured && <p className="mt-2 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function MeetingCard({ row, onClick }) {
  const src = row.source;
  const decisions = (row.decisions || []).length;
  const actions = (row.action_items || []).length;
  return (
    <button
      type="button"
      onClick={onClick}
      className="bento p-5 text-left hover:border-fuchsia-500/40 transition-all"
      data-testid={`meeting-row-${row.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded-full border ${SOURCE_STYLES[src] || "border-border"}`}>{SOURCE_LABEL[src] || src}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[row.status] || "border-border"}`}>
          {row.status || "unknown"}
        </span>
        {row.redacted && (
          <span className="text-xs px-2 py-0.5 rounded-full border bg-red-500/15 text-red-600 border-red-500/40">
            Redacted
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">{formatDate(row.start_time || row.created_at)}</span>
      </div>
      <h3 className="mt-2 text-lg font-medium">{row.topic || "Untitled meeting"}</h3>
      {row.summary && (
        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{row.summary}</p>
      )}
      {row.status === "missing_transcript" && (
        <p className="mt-2 text-xs text-amber-600 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {row.missing_transcript_hint}
        </p>
      )}
      {(decisions > 0 || actions > 0) && (
        <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {decisions} decisions</span>
          <span className="flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> {actions} action items</span>
        </div>
      )}
    </button>
  );
}

function MeetingDetail({ row, onDelete, canDelete }) {
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <PlayCircle className="w-5 h-5 text-fuchsia-500" />
          {row.topic}
        </DialogTitle>
        <DialogDescription>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${SOURCE_STYLES[row.source] || "border-border"}`}>
            {SOURCE_LABEL[row.source] || row.source}
          </span>{" "}
          · {formatDate(row.start_time || row.created_at)}
          {row.host_email && <> · Host: {row.host_email}</>}
        </DialogDescription>
      </DialogHeader>

      {row.status !== "processed" && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">{row.status?.replace(/_/g, " ")}</p>
            {row.missing_transcript_hint && <p className="mt-1 text-xs">{row.missing_transcript_hint}</p>}
          </div>
        </div>
      )}

      {row.summary && (
        <section>
          <h4 className="text-sm font-semibold mb-1">Summary</h4>
          <p className="text-sm text-muted-foreground">{row.summary}</p>
        </section>
      )}

      {(row.decisions || []).length > 0 && (
        <section data-testid="meeting-decisions-section">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Decisions
          </h4>
          <ul className="space-y-1.5">
            {row.decisions.map((d, idx) => (
              <li key={idx} className="text-sm border-l-2 border-emerald-500 pl-3">{d}</li>
            ))}
          </ul>
        </section>
      )}

      {(row.action_items || []).length > 0 && (
        <section data-testid="meeting-actions-section">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-sky-500" /> Action items
          </h4>
          <ul className="space-y-1.5">
            {row.action_items.map((ai, idx) => (
              <li key={idx} className="text-sm border-l-2 border-sky-500 pl-3">
                <span className="font-medium">{ai.owner || "unassigned"}</span>: {ai.task}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(row.key_points || []).length > 0 && (
        <section>
          <h4 className="text-sm font-semibold mb-2">Key points</h4>
          <ul className="space-y-1.5 list-disc pl-5 text-sm text-muted-foreground">
            {row.key_points.map((k, idx) => <li key={idx}>{k}</li>)}
          </ul>
        </section>
      )}

      {row.transcript_text && row.transcript_text !== "(redacted by privacy filter)" && (
        <section>
          <h4 className="text-sm font-semibold mb-1 flex items-center gap-2">
            <ScrollText className="w-4 h-4" /> Transcript
            <span className="text-xs text-muted-foreground">({row.transcript_len} chars)</span>
          </h4>
          <div className="bg-muted/40 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap max-h-64 overflow-y-auto">
            {row.transcript_text}
          </div>
        </section>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t">
        {row.doc_web_link && (
          <a href={row.doc_web_link} target="_blank" rel="noreferrer" className="text-sm text-fuchsia-500 hover:underline flex items-center gap-1">
            View in Google Docs <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        {canDelete && (
          <Button variant="outline" size="sm" onClick={onDelete} data-testid="meeting-delete-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        )}
      </div>
    </div>
  );
}

function MeetingsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3" role="status" aria-label="Loading meetings">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bento p-5 animate-pulse">
          <div className="flex gap-2">
            <div className="h-4 w-16 bg-muted rounded-full" />
            <div className="h-4 w-20 bg-muted rounded-full" />
            <div className="h-4 w-24 bg-muted rounded-full ml-auto" />
          </div>
          <div className="mt-3 h-5 w-2/3 bg-muted rounded" />
          <div className="mt-2 h-3 w-full bg-muted rounded" />
          <div className="mt-1 h-3 w-4/5 bg-muted rounded" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onSimulate, onScan, meetConnected }) {
  return (
    <div className="bento p-10 text-center empty-icon" data-testid="meetings-empty-state">
      <div className="empty-icon-halo" />
      <div className="empty-icon-halo empty-icon-halo-2" />
      <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-fuchsia-500/15 text-fuchsia-500">
        <Video className="w-8 h-8 empty-icon-glyph" />
      </div>
      <h3 className="mt-4 text-lg font-medium">No meetings yet</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        Connect your Zoom account (webhook to <code className="text-xs">/api/meetings/zoom/webhook</code>)
        or scan Google Drive for Meet transcript Docs. Native transcripts only — no Whisper, no audio upload.
      </p>
      <div className="mt-4 flex justify-center gap-2">
        <Button variant="outline" onClick={onScan} disabled={!meetConnected} data-testid="empty-scan-meet-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Scan Google Meet
        </Button>
        <Button onClick={onSimulate} data-testid="empty-simulate-btn">
          <FlaskConical className="w-4 h-4 mr-2" />
          Try with sample transcript
        </Button>
      </div>
    </div>
  );
}

const SAMPLE_VTT = `WEBVTT

1
00:00:00.500 --> 00:00:04.200
Ava Admin: Alright, let's kick off sprint planning for iteration 12.

2
00:00:04.500 --> 00:00:10.000
Marc Member: I'll take the authentication ticket. We agreed to ship OAuth by Friday.

3
00:00:10.500 --> 00:00:14.800
Ava Admin: Perfect. Vera, can you own the QA rollout plan?

4
00:00:15.000 --> 00:00:18.500
Vera Viewer: Yes, I'll draft it by Wednesday and share for review.

5
00:00:19.000 --> 00:00:24.000
Ava Admin: One decision — we're deferring the analytics dashboard to next sprint.

6
00:00:24.500 --> 00:00:28.000
Marc Member: Sounds good. Let's do a mid-week sync on Wednesday.
`;
